import { createContext, useContext, useRef, useState, useCallback, ReactNode } from "react";
import { stateWithCity } from "../model";
import type { WorkAuthorization } from "./CandidateResult";

export interface SkillEntry {
  id: number;
  name: string;
  importance: "Required" | "NiceToHave";
}
export interface JobTitleEntry {
  id: number;
  name: string;
}
export interface LocationType {
  State: string;
  City: string;
  Name?: string | null;
  label?: string;
  Zip?: any;
}

export interface SavedSearchState {
  searchType: string;
  jobTitleEntries: { id: number; name: string }[];
  skillEntries: SkillEntry[];
  location: any[];
  radius: string;
  booleanEntries: { name: string; importance: string };
  filters: any;
}

interface SearchFilterContextValue {
  // search type
  searchType: string;
  setSearchType: (v: string) => void;

  // boolean
  booleanEntries: { name: string; importance: string };
  setBooleanEntries: (v: { name: string; importance: string }) => void;

  // skills
  skillEntries: SkillEntry[];
  addSkill: () => void;
  removeSkill: (id: number) => void;
  updateSkillName: (id: number, name: string) => void;
  toggleSkillImportance: (id: number) => void;

  // job titles
  jobTitleEntries: JobTitleEntry[];
  addJobTitle: () => void;
  removeJobTitle: (id: number) => void;
  updateJobTitle: (id: number, name: string) => void;

  // location
  location: string[];
  setLocation: (v: string[]) => void;
  suggestions: LocationType[];
  searchLocation: (event: { query: string }) => void;
  radius: string;
  setRadius: (v: string) => void;

  // work auth
  workAuthorizations: WorkAuthorization[];
  setWorkAuthorizations: (v: WorkAuthorization[]) => void;

  // advanced filters (emitted by AdvancedOptionsInline)
  filters: any;
  setFilters: (v: any) => void;

  // reset
  clearAll: () => void;
  resetKey: number;
  restoreState: (state: SavedSearchState) => void;
  toast: React.MutableRefObject<any>;
}

const SearchFilterContext = createContext<SearchFilterContextValue | undefined>(undefined);

export const SearchFilterProvider = ({ children, toast }: { children: ReactNode; toast: React.MutableRefObject<any> }) => {
  const [searchType, setSearchType] = useState("semantic");
  const [booleanEntries, setBooleanEntries] = useState({ name: "", importance: "Required" });
  const [skillEntries, setSkillEntries] = useState<SkillEntry[]>([{ id: 1, name: "", importance: "NiceToHave" }]);
  const [jobTitleEntries, setJobTitleEntries] = useState<JobTitleEntry[]>([{ id: 1, name: "" }]);
  const [location, setLocation] = useState<string[]>([]);
  const [suggestions, setSuggestions] = useState<LocationType[]>([]);
  const [radius, setRadius] = useState("30");
  const [workAuthorizations, setWorkAuthorizations] = useState<WorkAuthorization[]>([]);
  const [filters, setFilters] = useState<any>({});
  const [resetKey, setResetKey] = useState(0);

  const nextSkillId = useRef(2);
  const nextJobTitleId = useRef(2);

  const restoreState = useCallback((state: SavedSearchState) => {
    const safeJobTitles = Array.isArray(state?.jobTitleEntries) && state.jobTitleEntries.length ? state.jobTitleEntries : [{ id: 1, name: "" }];
    const safeSkills = Array.isArray(state?.skillEntries) && state.skillEntries.length ? state.skillEntries : [{ id: 1, name: "", importance: "NiceToHave" as const }];
    const maxJobTitleId = Math.max(0, ...safeJobTitles.map((j) => j.id ?? 0));
    const maxSkillId = Math.max(0, ...safeSkills.map((s) => s.id ?? 0));
    nextJobTitleId.current = maxJobTitleId + 1;
    nextSkillId.current = maxSkillId + 1;
    setSearchType(state?.searchType ?? "semantic");
    setJobTitleEntries(safeJobTitles);
    setSkillEntries(safeSkills);
    setLocation(Array.isArray(state?.location) ? state.location : []);
    setRadius(state?.radius ?? "30");
    setBooleanEntries(state?.booleanEntries ?? { name: "", importance: "Required" });
    setFilters(state?.filters ?? {});
  }, []);
  const addSkill = useCallback(() => {
    setSkillEntries((prev) => {
      const last = prev[prev.length - 1];
      if (!last?.name?.trim()) {
        toast.current?.show({ severity: "warn", summary: "Empty Skill", detail: "Please enter a skill before adding another.", life: 2500 });
        return prev;
      }
      return [...prev, { id: nextSkillId.current++, name: "", importance: "NiceToHave" }];
    });
  }, [toast]);

  const removeSkill = useCallback((id: number) => {
    setSkillEntries((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const updateSkillName = useCallback((id: number, name: string) => {
    setSkillEntries((prev) => prev.map((s) => (s.id === id ? { ...s, name } : s)));
  }, []);

  const toggleSkillImportance = useCallback((id: number) => {
    setSkillEntries((prev) => prev.map((s) => (s.id === id ? { ...s, importance: s.importance === "Required" ? "NiceToHave" : "Required" } : s)));
  }, []);

  const addJobTitle = useCallback(() => {
    setJobTitleEntries((prev) => {
      const last = prev[prev.length - 1];
      if (!last?.name?.trim()) {
        toast.current?.show({ severity: "warn", summary: "Empty Job Title", detail: "Please enter a job title before adding another.", life: 2500 });
        return prev;
      }
      return [...prev, { id: nextJobTitleId.current++, name: "" }];
    });
  }, [toast]);

  const removeJobTitle = useCallback((id: number) => {
    setJobTitleEntries((prev) => prev.filter((j) => j.id !== id));
  }, []);

  const updateJobTitle = useCallback((id: number, name: string) => {
    setJobTitleEntries((prev) => prev.map((j) => (j.id === id ? { ...j, name } : j)));
  }, []);

  // debounced zip lookup so it doesn't fire an API call per keystroke
  const zipDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchLocation = useCallback(async (event: { query: string }) => {
    const query = event.query.trim().toLowerCase();
    if (!query) {
      setSuggestions([]);
      return;
    }

    const local: LocationType[] = stateWithCity?.filter((item: any) => item.State?.toLowerCase().includes(query) || (item?.Name && item.Name.toLowerCase().includes(query)) || (item?.City && item.City.toLowerCase().includes(query))) ?? [];
    setSuggestions(local.slice(0, 50));

    if (/^\d{3,5}$/.test(query)) {
      if (zipDebounce.current) clearTimeout(zipDebounce.current);
      zipDebounce.current = setTimeout(async () => {
        try {
          const zip = query.padEnd(5, "0").slice(0, 5);
          const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
          if (res.ok) {
            const data = await res.json();
            const place = data?.places?.[0];
            if (place) {
              setSuggestions((prev) => [{ City: place["place name"], State: place["state abbreviation"], Zip: data["post code"], label: `${place["place name"]}, ${place["state abbreviation"]} ${data["post code"]}` }, ...prev].slice(0, 50));
            }
          }
        } catch (err) {
          console.error("Zip lookup failed:", err);
        }
      }, 300);
    }
  }, []);

  const clearAll = useCallback(() => {
    setJobTitleEntries([{ id: 1, name: "" }]);
    setSkillEntries([{ id: 1, name: "", importance: "NiceToHave" }]);
    setBooleanEntries({ name: "", importance: "Required" });
    setLocation([]);
    setRadius("30");
    setFilters({});
    setResetKey((k) => k + 1);
  }, []);

  const value: SearchFilterContextValue = {
    searchType,
    setSearchType,
    booleanEntries,
    setBooleanEntries,
    skillEntries,
    addSkill,
    removeSkill,
    updateSkillName,
    toggleSkillImportance,
    jobTitleEntries,
    addJobTitle,
    removeJobTitle,
    updateJobTitle,
    location,
    setLocation,
    suggestions,
    searchLocation,
    radius,
    setRadius,
    workAuthorizations,
    setWorkAuthorizations,
    filters,
    setFilters,
    clearAll,
    resetKey,
    toast,
    restoreState,
  };

  return <SearchFilterContext.Provider value={value}>{children}</SearchFilterContext.Provider>;
};

export const useSearchFilters = () => {
  const ctx = useContext(SearchFilterContext);
  if (!ctx) throw new Error("useSearchFilters must be used within SearchFilterProvider");
  return ctx;
};
